/**
 * fxRoute — VIP's FX store, built from the data Worker's per-currency route
 * (WORK.md item 52 step A2) rather than read from the `fxStore` blob
 * market-data's mirror wrote into VIP's KV.
 *
 *   GET /api/symbols/fx?ccy=GBP,EUR&from=YYYY-MM-DD
 *   → { base: 'USD', from, rates: { CCY: { date: usdPerUnit } }, missing }
 *
 * Each `rates[CCY]` is exactly an fxStore entry (lib/calc/fx.ts FxStore), so
 * what this module assembles is read by every engine unchanged. Both the app
 * (app/src/lib/fxStoreCache.ts) and the shared view (functions/shared/
 * [token].ts) build their store here, so the two cannot ask for different
 * currencies or read the answer two ways.
 *
 * WHICH CURRENCIES. The route answers only what it is asked, and the blob held
 * every currency fx_rates quotes against USD, so VIP keeps that list itself:
 * FX_CURRENCIES, the 146 non-USD currencies the blob held on 2026-10-19 (147
 * with USD, read off the blob through the public data endpoint). A caller may add more; a
 * subunit (GBX) is asked as its major (GBP), and USD, the pivot, is never
 * asked. A currency the Worker does not hold comes back in `missing` and is
 * simply absent from the store, so `fxRate` answers null for it and every
 * page marks it "no FX rate": a missing rate is never 1 (lib/calc/fx.ts).
 *
 * A FAILED READ IS NOT A CLEAN ONE. An answer that is not the route's shape
 * throws FxAnswerError, never an empty store.
 */
import { canonicalCurrency, CURRENCY_SUBUNITS, type FxStore } from './calc/fx';

export const FX_ROUTE = '/api/symbols/fx';
/** The route's own default first date, fxStore's (held equal to the Worker's FX_FROM by tests/unit/fxRoute.test.ts). */
export const FX_FROM = '2007-01-01';
/** The most currencies the route answers in one request (held equal to the Worker's MAX_CCY by test). */
export const FX_MAX_CCY = 200;

/** Every non-USD currency the `fxStore` blob held on 2026-10-19 (146; USD is the pivot). */
export const FX_CURRENCIES: readonly string[] = Object.freeze([
  'AED', 'AFN', 'ALL', 'AMD', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN', 'BAM', 'BBD', 'BDT', 'BHD', 'BIF', 'BMD',
  'BND', 'BOB', 'BRL', 'BSD', 'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHF', 'CLF', 'CLP', 'CNH', 'CNY', 'COP',
  'CRC', 'CUP', 'CVE', 'CZK', 'DJF', 'DKK', 'DOP', 'DZD', 'EGP', 'ETB', 'EUR', 'FJD', 'FKP', 'GBP', 'GEL',
  'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD', 'HKD', 'HNL', 'HTG', 'HUF', 'IDR', 'ILS', 'INR', 'IQD', 'IRR',
  'ISK', 'JMD', 'JOD', 'JPY', 'KES', 'KHR', 'KMF', 'KRW', 'KWD', 'KYD', 'KZT', 'LAK', 'LBP', 'LKR', 'LRD',
  'LYD', 'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN', 'NAD',
  'NGN', 'NIO', 'NOK', 'NPR', 'NZD', 'OMR', 'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG', 'QAR', 'RON',
  'RSD', 'RUB', 'RWF', 'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLL', 'SOS', 'SVC', 'SZL', 'THB',
  'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TWD', 'TZS', 'UAH', 'UGX', 'UYU', 'UZS', 'VES', 'VND', 'XAF', 'XAG',
  'XAU', 'XBR', 'XCD', 'XDR', 'XOF', 'XPD', 'XPF', 'XPT', 'YER', 'ZAR', 'ZMW',
]);

const CCY_RE = /^[A-Z]{3}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export class FxAnswerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FxAnswerError';
  }
}

/**
 * The currencies to ask the route for: FX_CURRENCIES plus `extra`, each read
 * through `canonicalCurrency`, a subunit as its major, USD and anything that
 * is not a three-letter code left out; sorted, each once.
 */
export function fxRouteCurrencies(extra: Iterable<string | null | undefined> = []): string[] {
  const out = new Set<string>();
  for (const raw of [...FX_CURRENCIES, ...extra]) {
    const c = canonicalCurrency(raw);
    const major = CURRENCY_SUBUNITS[c]?.major ?? c;
    if (major !== 'USD' && CCY_RE.test(major)) out.add(major);
  }
  return [...out].sort();
}

/** The route's paths for `currencies`, at most FX_MAX_CCY a request. */
export function fxRoutePaths(currencies: readonly string[], from: string = FX_FROM): string[] {
  const paths: string[] = [];
  for (let i = 0; i < currencies.length; i += FX_MAX_CCY) {
    const q = new URLSearchParams({ ccy: currencies.slice(i, i + FX_MAX_CCY).join(','), from });
    paths.push(`${FX_ROUTE}?${q.toString()}`);
  }
  return paths;
}

export interface FxAnswer {
  /** USD per unit of each currency answered, by date: an fxStore. */
  store: FxStore;
  /** The currencies asked for that the Worker holds no series for, sorted. */
  missing: string[];
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Read one answer of the route. Throws FxAnswerError on anything but its
 * shape: a base other than USD, rates that are not a {CCY: {date: rate}} map
 * of finite positive numbers, a currency answered that was not asked, or a
 * `missing` that is not a list of codes. A currency asked and in neither
 * `rates` nor `missing` is counted missing.
 */
export function readFxAnswer(raw: unknown, asked: readonly string[]): FxAnswer {
  if (!isObject(raw)) throw new FxAnswerError('FX answer is not an object');
  if (raw.base !== 'USD') throw new FxAnswerError(`FX answer's base is ${JSON.stringify(raw.base)}, not USD`);
  if (!isObject(raw.rates)) throw new FxAnswerError('FX answer has no rates map');
  if (!Array.isArray(raw.missing) || !raw.missing.every((c) => typeof c === 'string' && CCY_RE.test(c))) {
    throw new FxAnswerError('FX answer has no missing list');
  }
  const want = new Set(asked);
  const store: FxStore = {};
  for (const [ccy, series] of Object.entries(raw.rates)) {
    if (!want.has(ccy)) throw new FxAnswerError(`FX answer holds ${ccy}, which was not asked for`);
    if (!isObject(series)) throw new FxAnswerError(`FX answer's ${ccy} series is not a map`);
    const out: Record<string, number> = {};
    for (const [date, rate] of Object.entries(series)) {
      if (!DATE_RE.test(date) || typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
        throw new FxAnswerError(`FX answer's ${ccy} holds ${JSON.stringify(date)}: ${JSON.stringify(rate)}`);
      }
      out[date] = rate;
    }
    store[ccy] = out;
  }
  const missing = new Set<string>(raw.missing as string[]);
  for (const c of asked) if (!(c in store)) missing.add(c);
  for (const c of Object.keys(store)) missing.delete(c);
  return { store, missing: [...missing].sort() };
}

/**
 * The whole store for `currencies`, one route read a chunk through `get`
 * (which answers the parsed JSON of a path, or throws). Throws on the first
 * read or answer that fails, so a partial store is never taken for a whole one.
 */
export async function buildFxStore(
  get: (path: string) => Promise<unknown>,
  currencies: readonly string[] = fxRouteCurrencies(),
  from: string = FX_FROM,
): Promise<FxAnswer> {
  const store: FxStore = {};
  const missing: string[] = [];
  for (let i = 0; i < currencies.length; i += FX_MAX_CCY) {
    const asked = currencies.slice(i, i + FX_MAX_CCY);
    const [path] = fxRoutePaths(asked, from);
    const answer = readFxAnswer(await get(path), asked);
    Object.assign(store, answer.store);
    missing.push(...answer.missing);
  }
  return { store, missing: missing.sort() };
}
